import React, { Component } from 'react';
import {
  fetchMFAPreference,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  confirmSignIn,
  signOut,
  resetPassword,
  confirmResetPassword,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import './customLogin.css';
import {
  PrimaryButton,
  SecondaryButton,
  NeutralButton,
} from './component/common';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import GoogleIcon from './component/Icon/GoogleIcon';
import axios from 'axios';
import config from '../config';
import AuthenticationManager from './auth/AuthenticationManager';
import QRCode from 'qrcode';

// AWS Amplify v6 configuration is handled globally

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

export class CustomSignIn extends Component {
  constructor(props) {
    super(props);
    this._validAuthStates = ['signIn', 'signedOut', 'signedUp', 'loading'];
    this.state = {
      username: '',
      resetUserName: '',
      password: '',
      errorMessage: '',
      openError: false,
      formState: 'signIn',
      newPassword: '',
      user: {},
      pageProcessing: false,
      loading: false,
      mfaChallengeCode: '',
      totpSecret: '',
      totpSetupUri: '',
      totpQrCodeDataUrl: '',
      totpCode: '',
      mfaChallengeSubmitting: false,
      mfaSetupSubmitting: false,
    };
    this.postMfaCompletion = null;
    this.otpInputRefs = {
      mfaChallengeCode: [],
      totpCode: [],
    };

    // AWS Amplify v6 is configured globally in App.js
  }

  componentDidMount = async () => {
    this.setState({
      username: '',
      password: '',
      formState: 'signIn',
      resetUserName: '',
      newPassword: '',
    });

    // Check if user is returning from unified login
    this.checkForReturnFromUnifiedLogin();

    // Set up session refresh
    this.refreshInterval = setInterval(this.refreshSession, 4 * 60 * 1000);

    try {
      const user = await getCurrentUser();
      if (user) {
        this.props.onStateChange('signedIn', {});
        this.updateLastLogged();
      }
    } catch (err) {
      console.log('No existing session:', err);
    }
  };

  componentWillUnmount() {
    // Clear interval when component unmounts
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  handleResetPassword = () => {
    window.location.href = `${config.ADMIN_DASHBOARD_URL}/forgotPassword`;
  };

  handleSetNewPassword = () => {
    this.setState({ formState: 'setNewPassword' });
  };

  handleFirstTimeSignIn = () => {
    this.setState({ formState: 'changePassword' });
  };

  handleFormSubmission = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (this.state.formState === 'signIn') {
      this.signIn();
    } else if (this.state.formState === 'resetPassword') {
      this.resetPassword();
    } else if (this.state.formState === 'setNewPassword') {
      this.setNewPassword();
    } else if (this.state.formState === 'changePassword') {
      this.changePassword();
    } else if (this.state.formState === 'verifyTotpChallenge') {
      this.confirmTotpChallenge();
    } else if (this.state.formState === 'setupTotp') {
      this.verifyTotpSetupCode();
    }
  };

  resendCode = () => {
    this.resetPassword();
  };

  resetPassword = () => {
    const resetUserName = this.state.resetUserName;
    resetPassword({ username: resetUserName })
      .then(() => {
        this.setState({ formState: 'setNewPassword' });
      })
      .catch((error) => {
        this.setState({ openError: true });
        this.setState({ errorMessage: error.message });
      });
  };

  setNewPassword = () => {
    const resetUserName = this.state.resetUserName;
    const code = this.state.code;
    const newPassword = this.state.newPassword;
    confirmResetPassword({
      username: resetUserName,
      confirmationCode: code,
      newPassword: newPassword,
    })
      .then(() => {
        signIn({ username: resetUserName, password: newPassword }).then((r) => {
          signOut().then((r) => {
            console.log('Initiating global logout');
            this.setState({
              formState: 'signIn',
              resetUserName: '',
              newPassword: '',
            });
            window.location.href = '/';
          });
        });
      })
      .catch((error) => {
        this.setState({ openError: true });
        this.setState({ errorMessage: error.message });
      });
  };

  changePassword = () => {
    confirmSignIn({
      challengeResponse: this.state.newChangePassword,
    })
      .then((result) => {
        if (this.isTotpChallenge(result)) {
          this.setState({ formState: 'verifyTotpChallenge' });
          return;
        }

        this.completeSignInWithMfaGate();
      })
      .catch((error) => {
        this.setState({ openError: true });
        this.setState({ errorMessage: error.message });
      });
  };

  isSoftwareTokenMfaEnabled = (mfaPreference) => {
    if (!mfaPreference) {
      return false;
    }

    const preferred = mfaPreference.preferred || mfaPreference.preferredMFA || '';
    const enabled = mfaPreference.enabled || mfaPreference.enabledMFATypes || [];
    const normalizedPreferred = String(preferred).toUpperCase();
    const normalizedEnabled = (Array.isArray(enabled) ? enabled : [enabled]).map((value) =>
      String(value).toUpperCase()
    );

    return (
      normalizedPreferred.includes('SOFTWARE_TOKEN_MFA') ||
      normalizedPreferred.includes('TOTP') ||
      normalizedEnabled.some(
        (value) => value.includes('SOFTWARE_TOKEN_MFA') || value.includes('TOTP')
      )
    );
  };

  isTotpChallenge = (signInResult) => {
    const step = signInResult?.nextStep?.signInStep;
    const challengeName = signInResult?.ChallengeName;
    return (
      step === 'CONFIRM_SIGN_IN_WITH_SOFTWARE_TOKEN_MFA' ||
      step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
      challengeName === 'SOFTWARE_TOKEN_MFA'
    );
  };

  getOtpDigits = (value) => {
    const sanitized = (value || '').replace(/\D/g, '').slice(0, 6);
    return Array.from({ length: 6 }, (_, i) => sanitized[i] || '');
  };

  setOtpValue = (fieldName, value) => {
    this.setState({ [fieldName]: (value || '').replace(/\D/g, '').slice(0, 6) });
  };

  focusOtpInput = (fieldName, index) => {
    const ref = this.otpInputRefs?.[fieldName]?.[index];
    if (ref && typeof ref.focus === 'function') {
      ref.focus();
      ref.select?.();
    }
  };

  handleOtpDigitChange = (fieldName, index, event) => {
    const nextChar = (event.target.value || '').replace(/\D/g, '').slice(-1);
    const digits = this.getOtpDigits(this.state[fieldName]);
    digits[index] = nextChar;
    const newValue = digits.join('');
    this.setOtpValue(fieldName, newValue);
    if (nextChar && index < 5) {
      this.focusOtpInput(fieldName, index + 1);
    }
    // Auto-submit when all 6 digits are entered
    if (newValue.length === 6 && /^\d{6}$/.test(newValue)) {
      setTimeout(() => this.handleFormSubmission(), 0);
    }
  };

  handleOtpKeyDown = (fieldName, index, event) => {
    if (event.key === 'Backspace') {
      const digits = this.getOtpDigits(this.state[fieldName]);
      if (!digits[index] && index > 0) {
        this.focusOtpInput(fieldName, index - 1);
      }
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      this.focusOtpInput(fieldName, index - 1);
    }
    if (event.key === 'ArrowRight' && index < 5) {
      this.focusOtpInput(fieldName, index + 1);
    }
  };

  handleOtpPaste = (fieldName, event) => {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    this.setOtpValue(fieldName, pasted);
    this.focusOtpInput(fieldName, Math.min(pasted.length, 6) - 1);
    // Auto-submit when pasted code is 6 digits
    if (pasted.length === 6) {
      setTimeout(() => this.handleFormSubmission(), 0);
    }
  };

  renderOtpInputs = (fieldName, disabled = false) => {
    const digits = this.getOtpDigits(this.state[fieldName]);
    return (
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-start', margin: '12px 0 8px' }}>
        {digits.map((digit, index) => (
          <TextField
            key={`${fieldName}-${index}`}
            value={digit}
            onChange={(event) => this.handleOtpDigitChange(fieldName, index, event)}
            onKeyDown={(event) => this.handleOtpKeyDown(fieldName, index, event)}
            onPaste={(event) => this.handleOtpPaste(fieldName, event)}
            inputRef={(ref) => {
              this.otpInputRefs[fieldName][index] = ref;
            }}
            disabled={disabled}
            variant="outlined"
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              autoComplete: 'one-time-code',
              style: { textAlign: 'center', fontSize: 22, fontWeight: 600, padding: '12px 0' },
            }}
            sx={{
              width: 48,
              '& .MuiOutlinedInput-root': {
                height: 56,
                borderRadius: '8px',
                background: '#fff',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
                '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(0,0,0,0.08)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#000', borderWidth: '1.5px' },
              },
            }}
          />
        ))}
      </div>
    );
  };

  checkMfaCompliance = async () => {
    let mfaPolicy;
    try {
      mfaPolicy = await this.send_request(`mfa-enforcement`, '', {}, 'GET');
    } catch (error) {
      return 'unknown';
    }

    if (!mfaPolicy?.data) {
      return 'unknown';
    }

    if (!mfaPolicy.data.mfaEnforced) {
      return 'compliant';
    }

    try {
      const mfaPreference = await fetchMFAPreference();
      return this.isSoftwareTokenMfaEnabled(mfaPreference) ? 'compliant' : 'needs_setup';
    } catch (error) {
      return 'unknown';
    }
  };

  startTotpSetup = async () => {
    try {
      const details = await setUpTOTP();
      const setupUriObject = details?.getSetupUri ? details.getSetupUri('Workflow86') : null;
      const setupUri = setupUriObject ? setupUriObject.toString() : '';
      let totpQrCodeDataUrl = '';
      if (setupUri) {
        try {
          totpQrCodeDataUrl = await QRCode.toDataURL(setupUri, { width: 240, margin: 1 });
        } catch {
          totpQrCodeDataUrl = '';
        }
      }

      if (!details?.sharedSecret && !setupUri) {
        throw new Error('Could not initialize authenticator setup. Please try again.');
      }

      this.totpSetupDetails = details;
      this.setState({
        formState: 'setupTotp',
        totpSecret: details?.sharedSecret || '',
        totpSetupUri: setupUri,
        totpQrCodeDataUrl,
      });
    } catch (error) {
      this.setState({ openError: true, errorMessage: error.message || 'Unable to set up MFA.' });
    }
  };

  completeSignInWithMfaGate = async () => {
    const compliance = await this.checkMfaCompliance();

    if (compliance === 'needs_setup') {
      await this.startTotpSetup();
      return;
    }

    // Fail closed: if the org policy or the user's MFA state cannot be verified,
    // do not grant access. Force re-authentication instead of silently allowing.
    if (compliance === 'unknown') {
      await signOut();
      this.setState({
        formState: 'signIn',
        pageProcessing: false,
        openError: true,
        errorMessage:
          "We couldn't verify your organization's security requirements. Please sign in again.",
      });
      return;
    }

    this.props.onStateChange('signedIn', {});
    this.setState({ pageProcessing: false });
    await this.updateLastLogged();
  };

  verifyTotpSetupCode = async () => {
    if ((this.state.totpCode || '').length !== 6) {
      this.setState({
        openError: true,
        errorMessage: 'Enter all 6 digits from your authenticator app.',
      });
      return;
    }

    this.setState({ mfaSetupSubmitting: true });
    try {
      await verifyTOTPSetup({ code: this.state.totpCode });
      await updateMFAPreference({ sms: 'DISABLED', totp: 'PREFERRED' });
      this.setState({ totpQrCodeDataUrl: '', totpSetupUri: '', totpSecret: '', totpCode: '' });

      await this.completeSignInWithMfaGate();
    } catch (error) {
      this.setState({
        openError: true,
        errorMessage: error.message || 'Unable to verify MFA code.',
      });
    } finally {
      this.setState({ mfaSetupSubmitting: false });
    }
  };

  confirmTotpChallenge = async () => {
    if ((this.state.mfaChallengeCode || '').length !== 6) {
      this.setState({
        openError: true,
        errorMessage: 'Enter all 6 digits to continue.',
      });
      return;
    }

    this.setState({ mfaChallengeSubmitting: true });
    try {
      const result = await confirmSignIn({ challengeResponse: this.state.mfaChallengeCode });
      if (result.isSignedIn) {
        await this.completeSignInWithMfaGate();
      }
    } catch (error) {
      this.setState({
        openError: true,
        errorMessage: error.message || 'The MFA code is invalid.',
      });
    } finally {
      this.setState({ mfaChallengeSubmitting: false });
    }
  };

  signIn = async () => {
    this.setState({
      formState: 'signIn',
      resetUserName: '',
      newPassword: '',
      pageProcessing: true,
    });

    const username = this.state.username;
    const password = this.state.password;

    try {
      // Attempt sign in
      const result = await signIn({ username, password });
      console.log('Sign in response:', result);

      if (
        result.nextStep &&
        result.nextStep.signInStep ===
          'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        console.log('New password required');
        this.setState({
          formState: 'changePassword',
          user: result,
          pageProcessing: false,
        });
        return;
      }

      if (this.isTotpChallenge(result)) {
        this.setState({
          formState: 'verifyTotpChallenge',
          pageProcessing: false,
        });
        return;
      }

      if (result.isSignedIn) {
        await this.completeSignInWithMfaGate();
      } else {
        this.setState({
          openError: true,
          errorMessage: `Unsupported sign-in step: ${result?.nextStep?.signInStep || 'unknown'}`,
          pageProcessing: false,
        });
      }
    } catch (error) {
      console.error('Sign in failed:', {
        code: error.code,
        name: error.name,
        message: error.message,
      });

      this.setState({
        openError: true,
        errorMessage: error.message || 'Sign in failed. Please try again.',
        pageProcessing: false,
      });
    }
  };

  updateLastLogged = async () => {
    try {
      console.log('Starting updateLastLogged request');
      const json = await this.send_request(
        `update-last-logged`,
        '',
        {},
        'POST'
      );

      if (!json) {
        console.log('UpdateLastLogged request returned null');
      } else {
        console.log('UpdateLastLogged response:', json);
      }
    } catch (err) {
      console.log('UpdateLastLogged error details:', {
        code: err.code,
        name: err.name,
        message: err.message,
        stack: err.stack,
      });
    }
  };

  serializeData = (formBodyObj) => {
    const requestBody = Object.keys(formBodyObj)
      .map((key) => {
        return (
          encodeURIComponent(key) + '=' + encodeURIComponent(formBodyObj[key])
        );
      })
      .join('&');

    return requestBody;
  };

  send_request = async (
    path,
    bodydata,
    querydata,
    method = 'POST',
    headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    responseType,
    onUploadProgress = null
  ) => {
    const url = `${config.USER_ADDRESS}/${path}`;
    console.log('Making request to:', url);

    try {
      console.log('Getting current session...');
      const session = await fetchAuthSession();

      if (!session.tokens?.idToken) {
        throw new Error('Invalid session');
      }

      headers.Authorization = `Bearer ${session.tokens.idToken.toString()}`;
      console.log('Token obtained');

      console.log('Sending request...');
      const response = await axios({
        url,
        method,
        data: bodydata,
        headers,
        responseType,
        onUploadProgress,
        timeout: 28000,
      });

      console.log('Request successful:', {
        status: response.status,
        hasData: response.data ? true : false,
      });
      return response;
    } catch (err) {
      console.error('Request failed:', {
        status: err.response && err.response.status,
        message: err.message,
        data: err.response && err.response.data,
        stack: err.stack,
      });
      throw err;
    }
  };

  handleClose = () => {
    this.setState({ openError: false });
  };

  backToSignIn = () => {
    this.setState({ formState: 'signIn' });
  };

  handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const check_type = ['radio', 'checkbox'].includes(type);
    this.setState({
      [name]: check_type ? checked : value,
      checkedValue: check_type ? value : null,
    });
  };

  // New method for unified login redirect
  redirectToUnifiedLogin = () => {
    // Use the new AuthenticationManager method for consistency
    AuthenticationManager.initiateUnifiedLogin();
  };

  // Modified signInWithGG to also redirect to unified login
  signInWithGG = async () => {
    this.setState({ loading: true });

    // Check if user is already authenticated on app.workflow86.com first
    try {
      const isAppAuthenticated =
        await AuthenticationManager.checkAppWorkflowAuthentication();
      if (isAppAuthenticated) {
        console.log(
          `User already authenticated on ${config.ADMIN_DASHBOARD_URL}, redirecting to unified login`
        );
        await AuthenticationManager.initiateUnifiedLogin();
        return;
      }
    } catch (error) {
      console.log(
        'Could not check app authentication status, proceeding with Google login redirect'
      );
    }

    // If not authenticated on app.workflow86.com, redirect to Google auth on app.workflow86.com
    const currentUrl = window.location.href;
    localStorage.setItem('form_return_url', currentUrl);

    const googleLoginUrl = `${config.ADMIN_DASHBOARD_URL}/auth/google?returnUrl=${encodeURIComponent(currentUrl)}`;
    window.location.href = googleLoginUrl;
  };

  // Add this method to handle token refresh
  refreshSession = async () => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        console.log('Session invalid, signing out');
        await signOut();
        window.location.reload();
      }
    } catch (err) {
      console.log('Session refresh error:', err);
    }
  };

  // Check if user is returning from unified login
  checkForReturnFromUnifiedLogin = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const loginSuccess = urlParams.get('loginSuccess');
    const token = urlParams.get('token');

    if (loginSuccess === 'true' && token) {
      try {
        // Store the token and refresh the session
        // This would need to be implemented based on how app.workflow86.com passes the token
        console.log('Returning from unified login with token');

        // Clean up URL parameters
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        // Trigger a session check
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.error('Error processing return from unified login:', error);
      }
    }
  };

  render() {
    return (
      <div>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={this.state.openError}
          autoHideDuration={6000}
          onClose={this.handleClose}
          key="topcenter"
        >
          <Alert onClose={this.handleClose} severity="error">
            {this.state.errorMessage}
          </Alert>
        </Snackbar>
        <div className="main-page">
          <div className="main-form-login">
            <div className="mainDiv">
              {this._validAuthStates.includes(this.props.authState) && (
                <form onSubmit={this.handleFormSubmission}>
                  {this.state.formState === 'signIn' && (
                    <>
                      <div style={{ marginBottom: 24 }}>
                        <img src="/svgs/logo.svg" alt="logo" className="logo-image" />
                      </div>
                      <h4>Sign in to access this form</h4>
                      <p className="auth-subtitle">
                        Enter your credentials to continue
                      </p>
                      <div className="inputArea">
                        <p className="inputDescription">Email or Username</p>
                        <TextField
                          id="username"
                          key="username"
                          name="username"
                          fullWidth
                          placeholder="name@company.com"
                          variant="outlined"
                          type="text"
                          size="small"
                          onChange={this.handleInputChange}
                          value={this.state.username}
                        />
                        <p className="inputDescription">Password</p>
                        <TextField
                          fullWidth
                          placeholder="Enter your password"
                          variant="outlined"
                          id="password"
                          key="password"
                          name="password"
                          type="password"
                          size="small"
                          onChange={this.handleInputChange}
                          value={this.state.password}
                        />
                      </div>
                      <div className="forgotUsernamePasswordText">
                        <Typography variant="body2">
                          <Link component="button" type="button" onClick={this.handleResetPassword} style={{ fontSize: 13, color: '#71717a', textDecoration: 'none' }}>
                            Forgot your password?
                          </Link>
                        </Typography>
                      </div>
                      <Button
                        className="signInButton"
                        type="submit"
                        disabled={this.state.pageProcessing}
                        variant="contained"
                        color="primary"
                        fullWidth
                      >
                        {this.state.pageProcessing ? '' : 'Sign In'}
                        {this.state.pageProcessing && (
                          <CircularProgress size={20} style={{ color: '#fff' }} />
                        )}
                      </Button>
                    </>
                  )}

                  {this.state.formState === 'changePassword' && (
                    <>
                      <div style={{ marginBottom: 24 }}>
                        <img src="/svgs/logo.svg" alt="logo" className="logo-image" />
                      </div>
                      <h4>Set new password</h4>
                      <p className="auth-subtitle">Choose a strong password for your account</p>
                      <div className="inputArea">
                        <p className="inputDescription">New Password</p>
                        <TextField
                          id="newChangePassword"
                          key="newChangePassword"
                          name="newChangePassword"
                          fullWidth
                          placeholder="Enter new password"
                          variant="outlined"
                          type="password"
                          size="small"
                          onChange={this.handleInputChange}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                        <Button variant="outlined" color="inherit" onClick={this.backToSignIn} style={{ borderRadius: 8, textTransform: 'none' }}>
                          Back
                        </Button>
                        <Button variant="contained" color="primary" type="submit" style={{ borderRadius: 8, textTransform: 'none', background: '#18181b', color: '#fff' }}>
                          Change password
                        </Button>
                      </div>
                    </>
                  )}

                  {this.state.formState === 'verifyTotpChallenge' && (
                    <>
                      <div style={{ marginBottom: 24 }}>
                        <img src="/svgs/logo.svg" alt="logo" className="logo-image" />
                      </div>
                      <h4>Two-factor authentication</h4>
                      <p className="auth-subtitle">
                        Enter the 6-digit code from your authenticator app to continue.
                      </p>
                      <div className="inputArea">
                        {this.renderOtpInputs('mfaChallengeCode', this.state.mfaChallengeSubmitting)}
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button variant="outlined" color="inherit" onClick={this.backToSignIn} disabled={this.state.mfaChallengeSubmitting} style={{ borderRadius: 8, textTransform: 'none' }}>
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={this.state.mfaChallengeSubmitting || (this.state.mfaChallengeCode || '').length !== 6}
                          style={{ borderRadius: 8, textTransform: 'none', background: '#18181b', color: '#fff', minWidth: 100 }}
                        >
                          {this.state.mfaChallengeSubmitting ? 'Verifying...' : 'Verify'}
                          {this.state.mfaChallengeSubmitting && (
                            <CircularProgress size={18} style={{ marginLeft: 8, color: '#fff' }} />
                          )}
                        </Button>
                      </div>
                    </>
                  )}

                  {this.state.formState === 'setupTotp' && (
                    <>
                      <div style={{ marginBottom: 24 }}>
                        <img src="/svgs/logo.svg" alt="logo" className="logo-image" />
                      </div>
                      <h4>Set up two-factor authentication</h4>
                      <p className="auth-subtitle">
                        Scan the QR code below with your authenticator app, then enter the verification code.
                      </p>
                      <div className="inputArea">
                        <div style={{ border: '1px solid #e4e4e7', borderRadius: 12, padding: 24, background: '#fafafa' }}>
                          {this.state.totpQrCodeDataUrl ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                              <img
                                src={this.state.totpQrCodeDataUrl}
                                alt="MFA setup QR code"
                                style={{ width: 200, height: 200, borderRadius: 12, border: '1px solid #e4e4e7', background: '#fff', padding: 12 }}
                              />
                            </div>
                          ) : null}
                          <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0 12px', gap: 12 }}>
                            <div style={{ flex: 1, height: 1, background: '#e4e4e7' }} />
                            <span style={{ fontSize: 12, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>or enter manually</span>
                            <div style={{ flex: 1, height: 1, background: '#e4e4e7' }} />
                          </div>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={this.state.totpSecret}
                            disabled
                            inputProps={{ style: { fontFamily: "'Courier New', Courier, monospace", fontSize: 13, letterSpacing: 2 } }}
                          />
                        </div>
                        <p className="inputDescription" style={{ marginTop: 16 }}>Verification code</p>
                        {this.renderOtpInputs('totpCode', this.state.mfaSetupSubmitting)}
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button variant="outlined" color="inherit" onClick={this.backToSignIn} disabled={this.state.mfaSetupSubmitting} style={{ borderRadius: 8, textTransform: 'none' }}>
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={this.state.mfaSetupSubmitting || (this.state.totpCode || '').length !== 6}
                          style={{ borderRadius: 8, textTransform: 'none', background: '#18181b', color: '#fff', minWidth: 100 }}
                        >
                          {this.state.mfaSetupSubmitting ? 'Verifying...' : 'Confirm'}
                          {this.state.mfaSetupSubmitting && (
                            <CircularProgress size={18} style={{ marginLeft: 8, color: '#fff' }} />
                          )}
                        </Button>
                      </div>
                    </>
                  )}

                  {this.state.formState === 'resetPassword' && (
                    <>
                      <div style={{ marginBottom: 24 }}>
                        <img src="/svgs/logo.svg" alt="logo" className="logo-image" />
                      </div>
                      <h4>Reset your password</h4>
                      <p className="auth-subtitle">We'll send a code to the email linked to your account.</p>
                      <div className="inputArea">
                        <p className="inputDescription">Username</p>
                        <TextField
                          id="resetUserName"
                          key="resetUserName"
                          name="resetUserName"
                          fullWidth
                          placeholder="Enter your username"
                          variant="outlined"
                          type="text"
                          size="small"
                          onChange={this.handleInputChange}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                        <Button variant="outlined" color="inherit" onClick={this.backToSignIn} style={{ borderRadius: 8, textTransform: 'none' }}>
                          Back
                        </Button>
                        <Button variant="contained" type="submit" style={{ borderRadius: 8, textTransform: 'none', background: '#18181b', color: '#fff', minWidth: 110 }}>
                          Send Code
                        </Button>
                      </div>
                    </>
                  )}

                  {this.state.formState === 'setNewPassword' && (
                    <>
                      <div style={{ marginBottom: 24 }}>
                        <img src="/svgs/logo.svg" alt="logo" className="logo-image" />
                      </div>
                      <h4>Enter reset code</h4>
                      <p className="auth-subtitle">Enter the code we sent and your new password.</p>
                      <div className="inputArea">
                        <p className="inputDescription">Reset code</p>
                        <TextField
                          id="code"
                          key="code"
                          name="code"
                          fullWidth
                          placeholder="Enter the 6-digit code"
                          variant="outlined"
                          type="text"
                          size="small"
                          onChange={this.handleInputChange}
                        />
                        <p className="inputDescription">New Password</p>
                        <TextField
                          fullWidth
                          placeholder="Enter your new password"
                          variant="outlined"
                          id="newPassword"
                          key="newPassword"
                          name="newPassword"
                          type="password"
                          size="small"
                          onChange={this.handleInputChange}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                        <Button variant="outlined" color="inherit" onClick={this.resendCode} style={{ borderRadius: 8, textTransform: 'none' }}>
                          Resend Code
                        </Button>
                        <Button variant="contained" type="submit" style={{ borderRadius: 8, textTransform: 'none', background: '#18181b', color: '#fff', minWidth: 100 }}>
                          Submit
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
